/**
 * The license matrix, one tenant at a time — the mobile form of the grid.
 *
 * A tenant × app matrix cannot usefully shrink: at phone width the row label
 * and the switch you are aiming at are never on screen together, so every tap
 * is a guess. Rotating it — one card per tenant, one row per app — keeps the
 * two things a decision needs (which app, which tenant) visible at once, at the
 * cost of seeing only one tenant at a time. That is the right trade on a phone,
 * where the task is "fix this one tenant", not "compare thirty".
 */
import {
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';

import { SEP_AMBER, SEP_BLUE, SEP_RED } from '@/theme';
import type { AppSummary, TenantRow } from '@/types/api';
import { describeCell, isExpiringSoon } from './cellState';

interface TenantLicenseCardProps {
  tenant: TenantRow;
  apps: AppSummary[];
  expanded: boolean;
  busy: Set<string>;
  failed: Set<string>;
  onToggleExpand: () => void;
  onToggle: (app: AppSummary, next: boolean) => void;
  children?: React.ReactNode;
}

export function TenantLicenseCard({
  tenant,
  apps,
  expanded,
  busy,
  failed,
  onToggleExpand,
  onToggle,
  children,
}: TenantLicenseCardProps) {
  const activeCount = apps.filter((app) => tenant.licenses[app.key]?.isActive).length;

  return (
    <Card>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="subtitle1" component="h2">
              {tenant.displayName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {tenant.key} · {tenant.userCount} Benutzer · {activeCount}/{apps.length} Apps
            </Typography>
          </Box>

          <Divider />

          <Stack divider={<Divider flexItem />}>
            {apps.map((app) => {
              const cell = tenant.licenses[app.key] ?? null;
              const state = describeCell(app, cell);
              const key = `${tenant.key}::${app.key}`;
              const saving = busy.has(key);
              const overrides = cell?.overrideCount ?? { allow: 0, deny: 0 };
              const overrideTotal = overrides.allow + overrides.deny;

              return (
                <Stack
                  key={app.key}
                  direction="row"
                  spacing={1}
                  // 48px rows: a switch on a phone needs a thumb-sized target,
                  // and the whole row reads as one tappable thing.
                  sx={{ minHeight: 48, alignItems: 'center', justifyContent: 'space-between' }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{ color: app.isEnabled ? 'text.primary' : 'text.disabled' }}
                      >
                        {app.name}
                      </Typography>
                      {!app.isEnabled && <Chip label="bald" variant="outlined" />}
                      {isExpiringSoon(cell) && (
                        <ScheduleOutlinedIcon sx={{ fontSize: 14, color: SEP_AMBER }} />
                      )}
                    </Stack>
                    {/* No hover on a touch screen, so the tooltip text that
                        carries the cell's meaning on desktop is shown inline. */}
                    <Typography variant="caption" color="text.secondary">
                      {state.tooltip}
                    </Typography>
                  </Box>

                  {saving ? (
                    <Box sx={{ width: 58, display: 'flex', justifyContent: 'center' }}>
                      <CircularProgress size={18} />
                    </Box>
                  ) : (
                    <Badge
                      badgeContent={overrideTotal || 0}
                      invisible={overrideTotal === 0}
                      sx={{
                        '& .MuiBadge-badge': {
                          backgroundColor: overrides.deny > 0 ? SEP_RED : SEP_BLUE,
                          color: '#fff',
                          fontSize: 10,
                          height: 16,
                          minWidth: 16,
                        },
                      }}
                    >
                      <Switch
                        checked={state.checked}
                        disabled={!state.interactive}
                        onChange={(event) => onToggle(app, event.target.checked)}
                        slotProps={{
                          input: { 'aria-label': `${app.name} für Mandant ${tenant.displayName}` },
                        }}
                        sx={{
                          ...(state.variant === 'expired' && {
                            '& .MuiSwitch-thumb': { backgroundColor: SEP_AMBER },
                          }),
                          ...(failed.has(key) && {
                            outline: `2px solid ${SEP_RED}`,
                            borderRadius: 1,
                          }),
                        }}
                      />
                    </Badge>
                  )}
                </Stack>
              );
            })}
          </Stack>

          <Box>
            <Button
              size="small"
              onClick={onToggleExpand}
              startIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
              {expanded ? 'Benutzer ausblenden' : `Benutzer (${tenant.userCount})`}
            </Button>
          </Box>
          <Collapse in={expanded} unmountOnExit>
            {children}
          </Collapse>
        </Stack>
      </CardContent>
    </Card>
  );
}
